import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import jwt_decode from "jwt-decode";
import axios from "axios";
import { saveUser } from "../network/cipAPI";

import AxiosInstance from "../network/axiosInstance";
import moment from "moment";
import { getItemSession } from "../utils/session";
import { API_URLS } from "../network/appUrls";
import { create_sfdc_engagement } from "../utils/SFDCexternalScript";
import { SFDC_ENGAGEMENT_API_URL } from "../utils/constantsString";

import {
  storeUsername,
  storeToken,
  storeDigital,
  storeUserEmail,
  storeUserDetails,
  storeuserRole,
  storeRequestStatus,
  storeCompanyName,
} from "../Store/TemplateStore/template.action";

export const SSOContext = React.createContext();
export const useSSO = () => useContext(SSOContext);

const SSOProvider = ({ children, onSetToken, ...initOptions }) => {
  const dispatch = useDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(true);

  let refreshInterval = null;
  const AUTH_ENDPOINT = process.env.REACT_APP_AUTH_ENDPOINT;
  const basePath = API_URLS.basePath;

  const navigate = useNavigate();
  useEffect(() => {
    const initSSO = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const error_param = queryParams.get("error");

      // check for the error in the URL
      if (error_param) {
        console.error("OAuth Error:", error_param);
        const path = `${basePath}unauthorized`;
        navigate(path); // Redirect to unauthorized page
        return;
      }

      try {
        console.log(
          "Initializing SSO",
          window.location.search,
          window.location.search.includes("code=")
        );

        if (!window.location.search.includes("code=")) {
          // User is not authenticated, redirect to Salesforce login
          const pkceResponse = await axios.get(
            process.env.REACT_APP_PKCE_ENDPOINT
          );
          const { code_challenge, code_verifier } = pkceResponse.data;
          sessionStorage.setItem("pkce_code_verifier", code_verifier);

          const redirectUrl = `${AUTH_ENDPOINT}?response_type=code&client_id=${
            process.env.REACT_APP_CONSUMER_KEY
          }&redirect_uri=${encodeURIComponent(
            initOptions.redirectUri
          )}&code_challenge=${code_challenge}&code_challenge_method=S256`;
          window.location.href = redirectUrl; // Redirect for authentication
          return;
        }

        const code_param = queryParams.get("code");
        if (code_param) {
          const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
          const getTokenParam = {
            code: code_param,
            client_id: process.env.REACT_APP_CONSUMER_KEY,
            redirect_uri: initOptions.redirectUri,
            grant_type: "authorization_code",
            code_verifier: codeVerifier,
          };
          const refreshToken = await getAuthorization(getTokenParam);
          refreshTokenTimer(refreshToken);
        }
      } catch (err) {
        console.log("Error during SSO initialization:", err);
      } finally {
        setLoading(false);
        setRefresh(false);
      }
    };
    //console.log("call init SSO")
    initSSO();
  }, []);

  async function refreshToken(token) {
    const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
    const getRefreshTokenParam = {
      client_id: process.env.REACT_APP_CONSUMER_KEY,
      refresh_token: token,
      grant_type: "refresh_token",
      code_verifier: codeVerifier,
    };
    await getAuthorization(getRefreshTokenParam);
  }

  const refreshTokenTimer = (refresh_token) => {
    setInterval(() => {
      refreshToken(refresh_token);
    }, refreshInterval);
  };

  async function getAuthorization(getTokenParam) {
    try {
      const response = await axios.post(process.env.REACT_APP_TOKEN, null, {
        params: getTokenParam,
      });
      if (response.data) {
        const { refresh_token, id_token } = response.data;
        const decodedAccessToken = jwt_decode(id_token);
        const { custom_attributes, exp } = decodedAccessToken;

        const { First_Name, Last_Name, UserCountry, UserEmail, CompanyName } =
          custom_attributes;

        const payload = {
          username: First_Name,
          email: UserEmail,
          country: UserCountry,
          role: null,
          industry: null,
          company: CompanyName,
        };
        dispatch(storeUsername(`${First_Name} ${Last_Name}`));
        dispatch(storeUserDetails(payload));
        if (CompanyName) {
          dispatch(storeCompanyName(CompanyName));
        }
        setIsAuthenticated(true);
        if (id_token) {
          dispatch(storeToken({ token: id_token }));
          const common = {
            Authorization: `Bearer ${id_token}`,
          };
          AxiosInstance.defaults.headers.common = common;
          setLoading(false);
          setIsAuthenticated(true);
          dispatch(storeUserEmail(`${UserEmail}`));
        // Create Salesforce engagement on sign-in if not already created in this session
        if (
          SFDC_ENGAGEMENT_API_URL &&
          !sessionStorage.getItem("isSigninEngagementCreated")
        ) {
          create_sfdc_engagement({
            engagementType: "Sign-in",
            engagementSubType: "Sign-in",
            engagementURL: process.env.REACT_APP_URL + basePath,
            engagementCreationDate: moment().format("YYYY-MM-DDTHH:mm:ssZ"),
            emailAddress: UserEmail,
            originURL: process.env.REACT_APP_URL + basePath,
          });
          sessionStorage.setItem("isSigninEngagementCreated", "true");
        }
          await saveUserDetails(payload);
        }
        const currentTime = moment().add(1, "minutes");
        const expireTime = moment.unix(exp);
        refreshInterval = expireTime.diff(currentTime, "milliseconds");
        return refresh_token;
      }
    } catch (error) {
      console.log("Error during authorization:", error);
    }
  }

  const saveUserDetails = async (payload) => {
    try {
      let response = await saveUser(payload);
      const { user_role, request_status } = response;
      dispatch(storeuserRole(`${user_role}`));
      dispatch(storeRequestStatus(request_status));
      if (request_status === -1 || request_status === 2) {
        var lastVisitedURL = getItemSession("lastVisitedURL");
        if (lastVisitedURL) {
          navigate(lastVisitedURL);
        } else {
          const path = `${basePath}plant`;
          navigate(path);
        }
      } else if (
        request_status === 1 ||
        request_status === 3 ||
        request_status === 4
      ) {
        const path = `${basePath}messagePage`;
        navigate(path);
      } else if (request_status === 0) {
        const path = `${basePath}requestAccess`;
        navigate(path);
      } else {
        const path = `${basePath}unauthorized`;
        navigate(path);
      }
    } catch (error) {
      console.error("Error saving user information:", error);
    }
  };

  return (
    <SSOContext.Provider value={{ isAuthenticated, loading, refresh }}>
      {children}
    </SSOContext.Provider>
  );
};

export default SSOProvider;
